<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('studentScoringEntry.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('toHomePage'), href: '/' },
          {
            text: $t('studentScoringEntry.pageTitle'),
            active: true
          }
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="d-flex justify-content-between">
          <div class="ts-space-x-3">
            <ts-button
              outline
              color="info"
              @click="$router.push({ name: 'student-scoring-entry-create' })"
              >{{ $t('add_new') }}</ts-button
            >
            <ts-button
              outline
              @click="$router.push({ name: 'student-scoring-entry-table' })"
              >{{ $t('studentScoringEntry.tableEntry') }}</ts-button
            >
          </div>
          <div class="d-flex">
            <ts-input
              v-model="search"
              :placeholder="$t('search')"
              style="width: 250px"
              class="inline-block"
            />
            <ts-button
              outline
              class="ml-2"
              v-tooltip="$t('filter')"
              @click.prevent="visible = true"
            >
              <i class="fa fa-filter"></i>
            </ts-button>
          </div>
        </div>
        <!-- drawer -->
        <a-drawer
          :title="$t('filter')"
          placement="right"
          :closable="false"
          :visible="visible"
          @close="() => (visible = false)"
          :zIndex="1020"
          width="300px"
        >
          <drawer-filter />
          <div class="row">
            <div class="col-md-12">
              <ts-button
                color="primary"
                class="mr-2"
                @click.prevent="fetchData"
                >{{ $t('view') }}</ts-button
              >
              <ts-button color="danger" @click.prevent="clearFilter">{{
                $t('studentScoringEntry.clear')
              }}</ts-button>
            </div>
          </div>
        </a-drawer>
      </ts-card-wrapper>
      <!-- table list -->
      <div class="overflow-auto">
        <ts-table
          :columns="columns"
          :records="resources"
          :loading="loading"
          :expandable="true"
          showGroupIcon
          groupIcon="fa fa-edit"
          :rowGroup="{
            field: 'class_name'
          }"
          @expand="onExpand"
          @actionGroup="onActionGroup"
        >
          <template v-slot="{ record }">
            <td>{{ record.branch.branch_name_en }}</td>
            <td>{{ record.student_name }}</td>
            <td>{{ record.cycle_value_text }}</td>
            <td>{{ record.grade_name_foreign }}</td>
            <td>{{ record.scoring_cycle_name }}</td>
            <td>{{ record.main_program }}</td>
            <td>{{ record.term_name }}</td>
            <td>{{ record.ac_year }}</td>
            <td>{{ record.score_type_text }}</td>
            <td class="ts-whitespace-no-wrap text-center">
              <a href="#" class="text-primary"
                @click.prevent="onStudentEdit(record)"
              >
                <i class="fa fa-edit"></i>
              </a>
            </td>
          </template>
          <div slot="expand" slot-scope="prop" class="ts-w-full">
            <DetailInTable :record="prop.record.scoring" />
          </div>
        </ts-table>
      </div>
      <!-- numeric scoring entry -->
      <a-modal
        v-if="numeric_scoring"
        v-model="numeric_scoring"
        :zIndex="1020"
        width="100%"
        wrap-class-name="full-modal"
        title="Numbering Scoring Entry"
        :footer="null"
      >
        <numeric-scoring-entry
          v-model="model"
          @closeModal="() => (numeric_scoring = false)"
          :class-operations="classOperations"
          :student-profiles="entry_on_student_profiles"
          :ignore-scoring-on-cycle="ignore_scoring_on_cycle"
          :enable-rating-on-cycle="enable_rating_on_cycle"
          :grade-id="paramsGetComponentForGrading.grade_id"
          :acps-id="paramsGetComponentForGrading.acps_id"
        />
      </a-modal>
      <!-- grading scoring entry -->
      <a-modal
        v-if="grading_scoring"
        v-model="grading_scoring"
        :zIndex="1020"
        title="Grading Scoring Entry"
        width="100%"
        wrap-class-name="full-modal"
        :footer="null"
      >
        <grading-scoring-entry
          v-model="model"
          @closeModal="() => (grading_scoring = false)"
          :params-get-component="paramsGetComponentForGrading"
          :class-operations="classOperations"
          :student-profiles="entry_on_student_profiles"
          :ignore-scoring-on-cycle="ignore_scoring_on_cycle"
          :enable-rating-on-cycle="enable_rating_on_cycle"
        />
      </a-modal>
      <!-- rating scoring entry -->
      <a-modal
        v-if="rating_scoring"
        v-model="rating_scoring"
        :zIndex="1020"
        title="Rating Scoring Entry"
        width="100%"
        wrap-class-name="full-modal"
        :footer="null"
      >
        <rating-scoring-entry
          v-model="model"
          @closeModal="() => (rating_scoring = false)"
          :params-get-component="paramsGetComponentForGrading"
          :class-operations="classOperations"
          :student-profiles="entry_on_student_profiles"
          :ignore-scoring-on-cycle="ignore_scoring_on_cycle"
          :enable-rating-on-cycle="enable_rating_on_cycle"
        />
      </a-modal>
      <div class="d-flex justify-content-end p-15">
        <ts-pagination
          v-model="pagination"
          @navigate="fetchData"
        ></ts-pagination>
      </div>
    </ts-card>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import { mapActions, mapState } from 'vuex'
import DrawerFilter from './components/drawer-filter'
import GradingScoringEntry from './components/grading-score-entry'
import NumericScoringEntry from './components/numeric-score-entry'
import RatingScoringEntry from './components/rating-score-entry'
import DetailInTable from './components/detail-in-table-list.vue'

import { getScoring } from '@/api/student-scoring-entry'

export default {
  name: 'studentScoringEntry',
  components: {
    DrawerFilter,
    GradingScoringEntry,
    NumericScoringEntry,
    RatingScoringEntry,
    DetailInTable
  },
  data() {
    return {
      numeric_scoring: false,
      grading_scoring: false,
      rating_scoring: false,
      entry_on_student: null,
      loading: false,
      visible: false,
      paramsGetComponentForGrading: {
        grade_id: 0,
        sc_bas_id: null,
        acps_id: null
      },
      model: {
        class_id: null, // add on param
        score_type: 1, // add on param but no use in DB
        st_class_id: null,
        acp_id: null,
        ac_year_id: null,
        ac_term_id: null,
        student_id: null,
        scoring_cycle_id: null,
        cycle_value: null,
        sc_subject_id: null
      },
      entry_on_student_profiles: null,
      ignore_scoring_on_cycle: [],
      enable_rating_on_cycle: []
    }
  },
  created() {
    this.fetchData()
    this.fetchClass()
  },
  computed: {
    ...mapState('studentScoringEntry', ['resources', 'pagination']),
    search: {
      get() {
        return this.$store.state.studentScoringEntry.search
      },
      set(newValue) {
        this.$store.commit('studentScoringEntry/SET_SEARCH', newValue)
        this.$store.commit('studentScoringEntry/RESET_CURRENT_PAGE')
      }
    },
    columns() {
      return [
        { name: this.$t('studentScoringEntry.branchName') },
        { name: this.$t('studentScoringEntry.studentName') },
        { name: this.$t('studentScoringEntry.cycle') },
        { name: this.$t('studentScoringEntry.academicGrade') },
        { name: this.$t('studentScoringEntry.scoringCycle') },
        { name: this.$t('studentScoringEntry.mainProgram') },
        { name: this.$t('studentScoringEntry.academicTerm') },
        { name: this.$t('studentScoringEntry.academicYear') },
        { name: this.$t('studentScoringEntry.scoreType') },
        { name: this.$t('actions'), style: 'text-align:center; width:7%' }
      ]
    }
  },
  methods: {
    ...mapActions('studentScoringEntry', [
      'getClassOperation',
      'getScoringCycle',
      'getScoringCycleValue'
    ]),
    fetchData(attributes) {
      this.loading = true
      this.$store
        .dispatch('studentScoringEntry/fetch', attributes)
        .then(() => {
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.$toasted.error(error.message)
        })
    },
    fetchClass() {
      this.loading = true
      this.getScoringCycle()
      this.getScoringCycleValue()
      this.getClassOperation()
        .then(response => {
          this.classes = response.data
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    clearFilter() {
      this.$store.commit('studentScoringEntry/RESET_FILTER')
      this.fetchData()
    },
    editScore(record) {
      var sc_bas_id = 1 // default is item base
      var scoring_cycle = record.scoring_cycle_id
      //ignore entry score for core subject on scoring cycle
      var ignore_scoring_on_cycle = record.academic_class.grade
        .ignore_scoring_on_cycle
        ? record.academic_class.grade.ignore_scoring_on_cycle.split(',')
        : []
      ignore_scoring_on_cycle.map(el => {
        this.ignore_scoring_on_cycle.push(parseInt(el))
      })
      //Enable rating for entry score for elective subject on scoring cycle
      var enable_rating_on_cycle = record.academic_class.grade
        .enable_rating_on_cycle
        ? record.academic_class.grade.enable_rating_on_cycle.split(',')
        : []
      enable_rating_on_cycle.map(el => {
        this.enable_rating_on_cycle.push(parseInt(el))
      })
      this.paramsGetComponentForGrading.acps_id = this.model.acps_id

      this.entry_on_student_profiles = record.student_profile
      this.entry_on_student = record.student_name
      this.model.student_id = record.student_id
      this.model.st_class_id = record.st_class_id
      this.model.class_id = record.class_id
      this.model.acp_id = record.acp_id
      this.model.ac_year_id = record.ac_year_id
      this.model.ac_term_id = record.ac_term_id
      this.model.scoring_cycle_id = record.scoring_cycle_id
      this.model.cycle_value = record.cycle_value

      if (record.score_type == 1) {
        // entry score for numberic
        if (scoring_cycle == 1) {
          sc_bas_id = record.grade.monthly_score_sc_bas_id
        } else if (scoring_cycle == 2) {
          sc_bas_id = record.grade.semester_score_sc_bas_id
        }

        this.numeric_scoring = true
        this.model.score_type = 1
        this.model.sc_bas_id = sc_bas_id
      } else if (record.score_type == 2) {
        // entry score for grading
        this.paramsGetComponentForGrading.grade_id = record.grade_id

        if (scoring_cycle == 1) {
          sc_bas_id = record.grade.monthly_score_sc_bas_id
        } else if (scoring_cycle == 2) {
          sc_bas_id = record.grade.semester_score_sc_bas_id
        }

        this.paramsGetComponentForGrading.sc_bas_id = sc_bas_id

        this.grading_scoring = true
        this.model.score_type = 2
      } else if (record.score_type == 3) {
        // entry score for rating
        this.paramsGetComponentForGrading.grade_id = record.grade_id

        if (scoring_cycle == 1) {
          sc_bas_id = record.monthly_score_sc_bas_id
        } else if (scoring_cycle == 2) {
          sc_bas_id = record.semester_score_sc_bas_id
        }

        this.paramsGetComponentForGrading.sc_bas_id = sc_bas_id

        this.rating_scoring = true
        this.model.score_type = 3
      }
    },
    onExpand(record) {
      let studsent = this.resources.find(
        r =>
          r.st_class_id === record.st_class_id &&
          r.cycle_value === record.cycle_value &&
          r.scoring_cycle_id === record.scoring_cycle_id
      )

      if (studsent.scoring !== undefined) return

      this.loading = true
      getScoring({
        student_id: record.student_id,
        class_id: record.class_id,
        st_class_id: record.st_class_id,
        scoring_cycle_id: record.scoring_cycle_id,
        cycle_value: record.cycle_value
      })
        .then(response => {
          studsent.scoring = response.data
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    onActionGroup(prop) {
      this.$router.push({ 
        name: 'student-scoring-entry-table-edit',
        query: {
          classId: prop.class_id,
          scoringCycleId: prop.scoring_cycle_id,
          cycleValue: prop.cycle_value,
          programStructureId: prop.program_structure_id
        }
      })
    },
    onStudentEdit(prop) {
      this.$router.push({ 
        name: 'student-scoring-entry-table-edit',
        query: {
          classId: prop.class_id,
          studentId: prop.student_id,
          scoringCycleId: prop.scoring_cycle_id,
          cycleValue: prop.cycle_value,
          programStructureId: prop.program_structure_id
        }
      })
    }
  },
  watch: {
    search: debounce(function() {
      this.fetchData({ branch_id: this.branch_id })
    }, 500)
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('studentScoringEntry/RESET_STATE')
    this.$store.commit('studentScoringEntry/RESET_FILTER')
    next()
  }
}
</script>
